import React, { useEffect, useState } from 'react';
import './SnapshotsPopup.scss';
import Rodal from '../../../components/Rodal/Rodal';
import { Input, ModalBody, ModalHeader, Row } from 'reactstrap';
import { useStateSelector } from '../../../store/selectors';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import createNotification from '../../../utils/createNotification';
import DataLoader from '../../../components/DataLoader/DataLoader';
import BootstrapTable, {
    SortOrder,
} from '../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import moment from 'moment';
import { DataTable } from '../../../models/DataRequestHub/DataTable';
import SortingOption from '../../../components/Table/models/bootstrap-table-sorting-option';
import { sortingFunction } from '../../../components/Table/models/table-helper';
import { DataForm } from '../../../models/DataRequestHub/DataForm';

interface SnapshotsPopupProps {
    projectId: number;
    objectId: number;
    viewId?: number;
    header: string;
    onClose(): void;
    triggerNext?: () => void;
    triggerBack?: () => void;
    setSelectedTableViewId?: (id: number) => void;
    setSelectedTable?: (table: DataTable) => void;
    setSelectedForm?: (form: DataForm) => void;
    isArchiveMode: boolean;
    snapshotFileName: string;
    isAdminView: boolean;
    isReadonlyMode: boolean;
    mode: 'table' | 'form';
}

export const SnapshotsPopup = (props: SnapshotsPopupProps) => {
    const mode = props.mode == 'form' ? 'dataForm' : 'dataTable';

    const axios = useStateSelector((state) => state.core.axios);

    const [snapshotName, setSnapshotName] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const [tableData, setTableData] = useState<any[]>([]);

    const [sortingOption, setSortingOption] = useState<SortingOption>({
        dataField: 'dateCreated',
        order: 'desc',
    });

    const addNewSnapshot = () => {
        setIsLoading(true);

        const params = {
            snapshotComment: snapshotName.trim(),
        };
        axios
            .post(
                `api/dataRequestProjects/${props.projectId}/${mode}s/${props.objectId}/${mode}Snapshots`,
                params
            )
            .then((response) => {
                if (response.status !== 200) {
                    createNotification(
                        'An error occured while creating snapshot',
                        'error'
                    );
                } else {
                    setSnapshotName('');
                    getSnapshotsData();
                }
            })
            .finally(() => setIsLoading(false));
    };

    const filterSnapshotsByView = (snapshots: DataTable[]) => {
        if (!props.viewId || props.mode !== 'table') {
            return snapshots;
        }
        return snapshots.filter((snapshot) =>
            snapshot.views?.some(
                (view) => view.originalTableViewId === props.viewId
            )
        );
    };

    const getSnapshotsData = () => {
        setIsLoading(true);

        axios
            .get(
                `api/dataRequestProjects/${props.projectId}/${mode}s/${props.objectId}/${mode}Snapshots`
            )
            .then((response) => {
                if (response.status !== 200) {
                    createNotification(
                        'An error occured while fetching snapshots',
                        'error'
                    );
                } else {
                    const filteredData = filterSnapshotsByView(response.data);
                    sortFunction(filteredData, sortingOption);
                }
            })
            .finally(() => setIsLoading(false));
    };

    const getSnapshotFile = (
        snapshotDate: Date,
        objectId: number,
        subObjectId?: number
    ) => {
        setIsLoading(true);
        const requestParamsObj = {
            params:
                props.mode == 'table'
                    ? { snapshotTableId: objectId, viewId: subObjectId }
                    : { snapshotFormId: objectId },
            responseType: 'blob' as const,
        };
        axios
            .get(
                `api/dataRequestProjects/${props.projectId}/${mode}s/${props.objectId}/${mode}Snapshots/file`,
                requestParamsObj
            )
            .then((response) => {
                if (response.status !== 200) {
                    createNotification(
                        'An error occured while downloading snapshot',
                        'error'
                    );
                } else {
                    const url = window.URL.createObjectURL(
                        new Blob([response.data])
                    );

                    const snapshoDateStringFormat =
                        moment(snapshotDate).format('MM-DD-YYYY');
                    const fileName = `${props.snapshotFileName} ${snapshoDateStringFormat}.xlsx`;
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            })
            .finally(() => setIsLoading(false));
    };

    const getDataTableActionsColumn = (row: DataTable) => (
        <div className="buttons-wrapper">
            <div
                className="link-text"
                onClick={() => {
                    if (props.viewId && props.setSelectedTableViewId) {
                        const view = row.views.find(
                            (x) => x.originalTableViewId === props.viewId
                        );
                        props?.setSelectedTableViewId(view.id);
                    }
                    if (props.setSelectedTable) props.setSelectedTable(row);
                    handleNextStep();
                }}>
                {' '}
                View
            </div>
            <div
                className="link-text"
                onClick={() => {
                    let viewId = 0;
                    if (props.viewId) {
                        const view = row.views.find(
                            (x) => x.originalTableViewId === props.viewId
                        );
                        viewId = view.id;
                    }
                    getSnapshotFile(row.dateCreated, row.id, viewId);
                }}>
                {'Download'}
            </div>
        </div>
    );

    const getDataFormActionsColumn = (row: DataForm) => (
        <div className="buttons-wrapper">
            <div
                className="link-text"
                onClick={() => {
                    props?.setSelectedForm(row);
                    handleNextStep();
                }}>
                {' '}
                View
            </div>
            <div
                className="link-text"
                onClick={() => {
                    getSnapshotFile(row.dateCreated, row.id);
                }}>
                Download
            </div>
        </div>
    );

    const getTableColumns = () => [
        { dataField: 'id', text: '', hidden: true },
        {
            dataField: 'dateCreated',
            text: 'Created Date',
            sort: true,
            formatter: (cellContent: Date) =>
                moment(cellContent).format('MM/DD/YYYY'),
        },
        {
            dataField: 'snapshotComment',
            text: 'Comment',
            sort: true,
        },
        {
            dataField: 'id',
            text: 'Actions',
            formatter: (_: number, row: any) => {
                if (props.mode == 'table')
                    return getDataTableActionsColumn(row as DataTable);
                else return getDataFormActionsColumn(row as DataForm);
            },
        },
    ];

    const handleNextStep = () => {
        if (props.triggerNext) {
            props.triggerNext();
        }
    };

    useEffect(() => {
        getSnapshotsData();
        if (props.setSelectedTable) props.setSelectedTable(null);
        if (props.setSelectedTableViewId) props.setSelectedTableViewId(null);
        if (props.setSelectedForm) props.setSelectedForm(null);
    }, []);

    const handleTableChange = (_props: any, state: any) => {
        const newSortingOption: SortingOption = {
            dataField: state.sortField,
            order: state.sortOrder as SortOrder,
        };
        setSortingOption(newSortingOption);
        sortFunction(state.data, newSortingOption);
    };

    const sortFunction = (data: any[], sortingOption: SortingOption) => {
        const sortedData = sortingFunction(data, sortingOption);
        setTableData(sortedData);
    };

    return (
        <Rodal
            visible={true}
            onClose={props.onClose}
            className="snapshot-popup"
            width={500}
            animation="fade"
            closeMaskOnClick={false}
            centered>
            <ModalHeader>{props.header}</ModalHeader>
            <ModalBody>
                {props.isArchiveMode ||
                !props.isAdminView ||
                props.isReadonlyMode ? (
                    <></>
                ) : (
                    <Row>
                        <div className="comment-input">
                            <Input
                                placeholder="Comment..."
                                value={snapshotName}
                                onChange={(event) =>
                                    setSnapshotName(event.target.value)
                                }
                            />
                            <ButtonLoader
                                onClick={() => {
                                    addNewSnapshot();
                                }}
                                className={'btn btn-primary'}
                                buttonText={'Add New Snapshot'}
                                loaderButtonText={''}
                                isLoading={isLoading}
                                disabled={!snapshotName}
                            />
                        </div>
                    </Row>
                )}
                <Row>
                    {isLoading ? (
                        <DataLoader />
                    ) : (
                        <BootstrapTable
                            striped
                            bordered
                            bootstrap4
                            remote
                            keyField="id"
                            defaultSorted={[sortingOption]}
                            onTableChange={handleTableChange}
                            data={tableData}
                            columns={getTableColumns()}
                            noDataIndication="No Data available"
                        />
                    )}
                </Row>
            </ModalBody>
        </Rodal>
    );
};
